/**
 * Shared collapsible filter panel.
 *
 * Wraps a page's filter controls in a Paper with a header row that collapses
 * the controls down to a one-line summary ("N active filters") behind a
 * chevron. The collapsed state is a per-page preference persisted in the
 * `panel_states` namespace (an open Dict[str, bool]; `true` = collapsed) under
 * the caller's `panelKey`, e.g. 'timers_filter_bar'. The namespace is replaced
 * wholesale on every write, so writes spread the existing keys to preserve the
 * other panels' state. Without a PreferencesProvider (component tests,
 * unauthenticated screens) the state is session-local only.
 */
import React, { ReactNode, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { usePreferences } from '../contexts/PreferencesContext';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

interface CollapsibleFilterBarProps {
  /** panel_states key for this page's filter bar, e.g. 'timers_filter_bar'. */
  panelKey: string;
  /** How many filters are currently active; shown as a badge and summary. */
  activeFilterCount: number;
  title?: string;
  /**
   * The noun the summary and aria copy speak — 'filter' by default. A bar
   * wrapping something that is not a filter (the calendar's layer toggles)
   * passes its own noun so the copy stays honest.
   */
  itemNoun?: string;
  itemNounPlural?: string;
  children: ReactNode;
}

const CollapsibleFilterBar: React.FC<CollapsibleFilterBarProps> = ({
  panelKey,
  activeFilterCount,
  title = 'Filters',
  itemNoun = 'filter',
  itemNounPlural,
  children,
}) => {
  const { panelPaddingSx, sectionGapSx } = useLayoutTokens();
  const store = usePreferences();
  const [localCollapsed, setLocalCollapsed] = useState(false);

  const panelStates = (store?.settings.panel_states ?? {}) as Record<string, unknown>;
  const collapsed = store ? panelStates[panelKey] === true : localCollapsed;

  const toggle = () => {
    const next = !collapsed;
    setLocalCollapsed(next);
    if (store) {
      // panel_states is replaced wholesale on every write — carry the other
      // panels' keys forward so toggling one bar never resets another.
      store.setNamespace('panel_states', { ...panelStates, [panelKey]: next });
    }
  };

  const plural = itemNounPlural ?? `${itemNoun}s`;
  const summaryText =
    activeFilterCount === 0
      ? `No active ${plural}`
      : activeFilterCount === 1
        ? `1 active ${itemNoun}`
        : `${activeFilterCount} active ${plural}`;

  return (
    <Paper sx={[panelPaddingSx, sectionGapSx]}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2">{title}</Typography>
          {activeFilterCount > 0 && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={activeFilterCount}
              aria-label={summaryText}
            />
          )}
          {collapsed && (
            <Typography variant="body2" color="text.secondary">
              {summaryText}
            </Typography>
          )}
        </Stack>
        <Tooltip title={collapsed ? `Expand ${plural}` : `Collapse ${plural}`}>
          <IconButton
            size="small"
            onClick={toggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? `expand ${plural}` : `collapse ${plural}`}
          >
            {collapsed ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ExpandLessIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <Collapse in={!collapsed} unmountOnExit>
        <Box sx={{ pt: 1.5 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

export default CollapsibleFilterBar;
